import { Grid, Typography } from '@mui/material';
import { useState, useMemo } from 'react';
import { useQueryClient } from 'react-query';

// Our Components
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import SoraTextField from 'components/Input/SoraTextField';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Our utils
import { clearFormValues, isSubmissionReady } from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our Emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our Constants
import { NUMBER } from 'components/Input/Types';
import { SBLOC_TYPE } from 'shared/constants/LoanInquiry/LoanInquiryTypes';

// Our PropTypes
import {
	advisorInfoProps,
	clientInfoProps,
	financialDataProps,
	lendingPropTypes
} from 'shared/propTypes/lendingPropTypes';

function SblocLoanForm({
	advisorInfo,
	clientInfo,
	isClientNameProvided,
	financialData,
	loanType,
	loanRequestId
}) {
	const queryClient = useQueryClient();
	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const createDeal = useMutateCreateDeal();
	const sendGenericEmail = useMutateSendGenericEmail();
	const updateLoanRequest = useMutateUpdateLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Advisor Info
	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	// Client Info
	const { clientFullName, creditScoreValue, clientId, firstName, lastName } =
		clientInfo;

	// Pre-populated state items
	const [totalValueInvestableAssets, setTotalValueInvestableAssets] =
		useState('');
	const [totalValueTaxableAssets, setTotalValueTaxableAssets] = useState('');
	const [loanAmount, setLoanAmount] = useState('');

	// Other fields
	const [additionalNotes, setAdditionalNotes] = useState('');

	// Modal
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	// Required Fields for Other Loan Form
	const loanRequestDetailsRequiredFields = [loanAmount];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(loanRequestDetailsRequiredFields) &&
			isClientNameProvided,
		loanRequestDetailsRequiredFields
	);

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			clientId,
			creditScoreValue,
			firstName,
			lastName,
			loanAmount,
			offerType: 'OTHER',
			tradelineType: SBLOC_TYPE,
			investableAssetsValue: +totalValueInvestableAssets,
			taxableAccountAssetsValue: +totalValueTaxableAssets,
			desiredLoanAmount: +loanAmount
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];

		const subject = `Securities Based Line Request - ${clientFullName}`;

		const message = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Total value of Investable Assets: ${totalValueInvestableAssets}`,
				`Total value of Assets in a Taxable Account (Non-Retirement): ${totalValueTaxableAssets}`
			],
			additionalNotes,
			lendingTypeRequested: 'Securities Based Line'
		});

		sendGenericEmail.mutate(
			{ firstName, lastName, subject, message, sendToArray },
			{
				onSuccess: () => {
					clearFormValues([setAdditionalNotes, setLoanAmount]);
				}
			}
		);
	};

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: loanAmount,
				label: 'Desired loan amount:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				${formattedCollection}
				------------------------------------------------------------------------------------------------
				Advisor's notes: ${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: 'SBLOC',
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest(loanRequestId);
		}
	};

	return (
		<>
			{savingLoanInquiry && <Loader />}

			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						Securities Based Line
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{!savingLoanInquiry && (
				<>
					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />
					<Grid item xs={12} md={6}>
						<CurrencyTextInput
							label="Total value of Investable Assets"
							value={totalValueInvestableAssets}
							onChange={setTotalValueInvestableAssets}
							type={NUMBER}
							inputProps={{
								'data-test': 'totalValueInvestableAssets'
							}}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<CurrencyTextInput
							label="Total Value of Assets in a Taxable Account (Non-Retirement)"
							value={totalValueTaxableAssets}
							onChange={setTotalValueTaxableAssets}
							type={NUMBER}
							inputProps={{
								'data-test': 'totalValueTaxableAssets'
							}}
						/>
					</Grid>{' '}
					<Grid item xs={12} md={6}>
						<CurrencyTextInput
							label="Desired Loan Amount *"
							value={loanAmount}
							onChange={setLoanAmount}
							type={NUMBER}
							inputProps={{
								'data-test': 'loanAmount'
							}}
						/>
					</Grid>
					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
					<Grid item xs={12}>
						{!isFormReady && (
							<RequiredFieldsTooltip>
								<span>
									<PrimaryButton
										disabled={!isFormReady}
										onClick={submitForm}
									>
										Submit
									</PrimaryButton>
								</span>
							</RequiredFieldsTooltip>
						)}
						{isFormReady && (
							<PrimaryButton onClick={submitForm}>
								Submit
							</PrimaryButton>
						)}
					</Grid>
				</>
			)}
		</>
	);
}

SblocLoanForm.propTypes = {
	...lendingPropTypes,
	advisorInfo: advisorInfoProps.isRequired,
	clientInfo: clientInfoProps.isRequired,
	financialData: financialDataProps.isRequired
};

export default SblocLoanForm;
